/**
 * EnergyDailyChart Component
 *
 * Line chart showing daily electricity consumption (kWh) over the last 30 days.
 */

import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts'

function formatDate(dateStr) {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })
}

function CustomTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null

  const date = new Date(label)
  const dateStr = date.toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'long' })

  return (
    <div className="bg-white border border-neutral-200 rounded-lg shadow-lg p-3">
      <p className="font-medium text-neutral-900 mb-2">{dateStr}</p>
      <div className="flex items-center justify-between gap-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <span className="text-neutral-600">Consumption</span>
        </div>
        <span className="font-medium text-neutral-900">
          {new Intl.NumberFormat('en-GB', {
            minimumFractionDigits: 1, maximumFractionDigits: 1
          }).format(payload[0].value)} kWh
        </span>
      </div>
    </div>
  )
}

export default function EnergyDailyChart({ daily, isLoading }) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Daily Consumption</CardTitle>
        </CardHeader>
        <CardContent>
          <Skeleton className="h-[320px] w-full" />
        </CardContent>
      </Card>
    )
  }

  if (!daily?.length) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Daily Consumption</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[320px] text-neutral-500">
            No data available
          </div>
        </CardContent>
      </Card>
    )
  }

  const chartData = daily.map(d => ({
    date: d.date,
    kwh: parseFloat(d.total_kwh)
  }))

  return (
    <Card>
      <CardHeader>
        <CardTitle>Daily Consumption</CardTitle>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis
              dataKey="date"
              tickFormatter={formatDate}
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fontSize: 12, fill: '#6b7280' }}
              tickFormatter={(v) => `${v} kWh`}
            />
            <Tooltip content={<CustomTooltip />} />
            <Line
              type="monotone"
              dataKey="kwh"
              name="Consumption"
              stroke="#f59e0b"
              strokeWidth={2}
              dot={{ fill: '#f59e0b', r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  )
}
